import React, { useRef, useState, useCallback } from 'react';
import { uploadToS3 } from '../services/apiClient';
import {
  getProfilePictureUploadUrl,
  finalizeProfilePicture,
  deleteProfilePicture,
} from '../services/accountService';

interface ProfilePictureUploadProps {
  currentPictureUrl: string | null;
  initial: string;
  backgroundColor: string;
  onPictureUpdated: (newUrl: string | null) => void;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentPictureUrl,
  initial,
  backgroundColor,
  onPictureUpdated,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = useCallback((file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Type de fichier non supporté. Utilisez JPEG, PNG ou WebP.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Le fichier est trop volumineux. Taille maximale : 5 Mo.';
    }
    return null;
  }, []);

  const handleUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL
      const urlResponse = await getProfilePictureUploadUrl(file.name, file.type);
      if (!urlResponse.success || !urlResponse.data) {
        throw new Error(urlResponse.error || 'Impossible d\'obtenir l\'URL d\'upload');
      }

      const { presigned_url, file_key } = urlResponse.data;

      // Step 2: Upload to S3
      const uploadResult = await uploadToS3(
        presigned_url,
        file,
        (percent) => setUploadProgress(percent),
        undefined,
        file.type
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Échec de l\'upload');
      }

      // Step 3: Finalize
      const finalizeResponse = await finalizeProfilePicture(file_key);
      if (!finalizeResponse.success || !finalizeResponse.data) {
        throw new Error(finalizeResponse.error || 'Échec de la finalisation');
      }

      onPictureUpdated(finalizeResponse.data.profile_picture_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [validateFile, onPictureUpdated]);

  const handleDelete = useCallback(async () => {
    setError(null);
    setIsDeleting(true);

    try {
      const response = await deleteProfilePicture();
      if (!response.success) {
        throw new Error(response.error || 'Échec de la suppression');
      }
      onPictureUpdated(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  }, [onPictureUpdated]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleAvatarClick = useCallback(() => {
    if (!isUploading && !isDeleting) {
      fileInputRef.current?.click();
    }
  }, [isUploading, isDeleting]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleAvatarClick();
    }
  }, [handleAvatarClick]);

  return (
    <div className="profile-picture-upload">
      <div
        className={`profile-avatar-container ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleAvatarClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label="Changer la photo de profil"
      >
        <div className="profile-avatar-large" style={{ backgroundColor }}>
          {currentPictureUrl ? (
            <img src={currentPictureUrl} alt="Photo de profil" />
          ) : (
            initial
          )}
        </div>

        {/* Overlay on hover */}
        <div className="profile-avatar-upload-overlay">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </div>

        {/* Upload progress overlay */}
        {isUploading && (
          <div className="profile-avatar-progress-overlay">
            <div className="profile-picture-progress">
              <div
                className="profile-picture-progress-bar"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="profile-avatar-progress-text">{uploadProgress}%</span>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <div className="profile-picture-actions">
        <button
          type="button"
          className="profile-button profile-button-secondary profile-button-small"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || isDeleting}
        >
          {isUploading ? (
            <>
              <span className="profile-button-spinner" />
              Envoi...
            </>
          ) : (
            'Changer la photo'
          )}
        </button>

        {currentPictureUrl && (
          <button
            type="button"
            className="profile-button profile-button-text profile-button-small"
            onClick={handleDelete}
            disabled={isUploading || isDeleting}
          >
            {isDeleting ? 'Suppression...' : 'Supprimer'}
          </button>
        )}
      </div>

      {error && (
        <div className="profile-picture-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
